/**
 * Ocesha as an embedded SHOPLINE app.
 *
 * Inside the SHOPLINE admin, Ocesha runs in an iframe:
 *  - no Ocesha login: /shopline/app exchanges a one-time code for a JWT, which is
 *    sent as "Authorization: Bearer" on every API call (third-party cookies are
 *    blocked inside iframes in Safari and others);
 *  - Stripe Checkout and social logins refuse to load in iframes, so those steps
 *    open in a small popup; when the popup lands back on Ocesha, its URL is relayed
 *    to the iframe through the backend and the iframe continues from there.
 *
 * Outside the SHOPLINE admin every function here is a no-op.
 */
import axios from "axios";

const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const API = `${API_BASE}/api/integrations/shopline`;

const K = {
  mode: "ocesha_embedded",
  token: "ocesha_embedded_token",
  tokenExp: "ocesha_embedded_token_exp",
  handle: "ocesha_embedded_handle",
  host: "ocesha_embedded_host",
  popupRelay: "ocesha_popup_relay",
};
const POPUP_NAME = "ocesha_shopline_popup";
const POPUP_FEATURES = "width=560,height=760,menubar=no,toolbar=no";
const RELAY_POLL_MS = 2000;
const RELAY_MAX_MS = 30 * 60 * 1000;

// ------------------------------------------------------------------ storage helpers
const ls = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};
const ss = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k: string) => { try { sessionStorage.removeItem(k); } catch { /* ignore */ } },
};

export const isInIframe = (): boolean => {
  try { return window.self !== window.top; } catch { return true; }
};

/** True when Ocesha is running inside the SHOPLINE admin. */
export const isEmbedded = (): boolean => isInIframe() && ls.get(K.mode) === "shopline";

export const getEmbeddedHandle = () => ls.get(K.handle) || "";
export const getEmbeddedHost = () => ls.get(K.host) || (getEmbeddedHandle() ? `${getEmbeddedHandle()}.myshopline.com` : "");

export const getEmbeddedToken = (): string | null => {
  if (!isEmbedded()) return null;
  const exp = Number(ls.get(K.tokenExp) || 0);
  if (exp && Date.now() > exp) return null;
  return ls.get(K.token);
};

const randomId = () => {
  const a = new Uint8Array(24);
  (window.crypto || (window as any).msCrypto).getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 40);
};

// ------------------------------------------------------------------ session
export interface EmbeddedSession {
  access_token: string;
  expires_in: number;
  store_handle: string;
  user: {
    user_email: string; name: string; role: string; free_plan: number;
    remaining_wish_count: number; subscription_level: string; expired: boolean;
    payment_status: string; partner_name: string; partner_type: string; course_id: string;
  };
}

export const markEmbedded = (handle: string, host?: string) => {
  ls.set(K.mode, "shopline");
  if (handle) ls.set(K.handle, handle);
  if (host) ls.set(K.host, host);
};

/** Same localStorage values the sign-in page sets, so the whole dashboard just works. */
export const startEmbeddedSession = (s: EmbeddedSession) => {
  markEmbedded(s.store_handle);
  ls.set(K.token, s.access_token);
  ls.set(K.tokenExp, String(Date.now() + (s.expires_in - 60) * 1000));
  const u = s.user;
  ls.set("logged_in", "true");
  ls.set("user_email", u.user_email);
  ls.set("free_plan", String(u.free_plan ?? ""));
  ls.set("remaining_wish_count", String(u.remaining_wish_count ?? 0));
  ls.set("userRole", u.role || "user");
  ls.set("partner_name", u.partner_name || "");
  ls.set("partner_type", u.partner_type || "");
  ls.set("course_id", u.course_id || "");
  ls.set("subscription_level", u.subscription_level || "");
  ls.set("expired", String(!!u.expired));
  ls.set("payment_status", u.payment_status || "");
  // SHOPLINE merchants skip Ocesha's sign-up onboarding entirely
  ls.del("FromSignUp");
  ls.del("CreatingfromLanding");
};

export const exchangeSessionCode = async (code: string): Promise<EmbeddedSession> => {
  const { data } = await axios.post(`${API}/embedded/session`, new URLSearchParams({ code }));
  return data;
};

// ------------------------------------------------------------------ auth header on API calls
const isApiUrl = (url: string) => {
  if (!API_BASE) return false;
  try { return new URL(url, window.location.href).href.startsWith(API_BASE); } catch { return false; }
};

let networkingInstalled = false;
export const installEmbeddedNetworking = () => {
  if (networkingInstalled || !isInIframe()) return;
  networkingInstalled = true;

  axios.interceptors.request.use((config) => {
    const token = getEmbeddedToken();
    const full = `${config.baseURL || ""}${config.url || ""}`;
    if (token && isApiUrl(full)) {
      config.headers = config.headers || {};
      if (!(config.headers as any).Authorization) (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const token = getEmbeddedToken();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (!token || !isApiUrl(url)) return origFetch(input as any, init);
    if (input instanceof Request) {
      const headers = new Headers(input.headers);
      if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
      return origFetch(new Request(input, { headers }), init);
    }
    const headers = new Headers(init?.headers || {});
    if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return origFetch(input as any, { ...(init || {}), headers });
  };
};

// ------------------------------------------------------------------ App Bridge
let bridgeApp: any = null;
const getBridge = async () => {
  if (bridgeApp) return bridgeApp;
  const appKey = ls.get("ocesha_embedded_app_key") || "";
  if (!appKey) return null;
  const mod: any = await import("@shoplineos/app-bridge");
  const Client = mod.default || mod;
  bridgeApp = Client.createApp({ appKey, host: getEmbeddedHost() || undefined });
  return bridgeApp;
};
export const rememberAppKey = (appKey: string) => { if (appKey) ls.set("ocesha_embedded_app_key", appKey); };

/** Start SHOPLINE OAuth from inside the admin (App Bridge), else redirect the top window. */
export const startShoplineOauth = async (opts: { appKey: string; scope: string; redirectUri: string; authUrl: string }): Promise<boolean> => {
  rememberAppKey(opts.appKey);
  try {
    const mod: any = await import("@shoplineos/app-bridge");
    const Client = mod.default || mod;
    const app = Client.createApp({ appKey: opts.appKey, host: getEmbeddedHost() || undefined });
    mod.Oauth.create(app).invoke({ scope: opts.scope, appKey: opts.appKey, redirectUri: opts.redirectUri });
    return true;
  } catch (e) {
    console.warn("[shopline] App Bridge OAuth failed, falling back to top redirect", e);
    try { (window.top as Window).location.href = opts.authUrl; return true; } catch { return false; }
  }
};

/** Navigate the SHOPLINE admin (top window) to a SHOPLINE URL. */
export const topRedirect = async (url: string) => {
  try {
    const app = await getBridge();
    if (app) {
      const mod: any = await import("@shoplineos/app-bridge");
      mod.Redirect.create(app).replaceTo(url);
      return;
    }
  } catch { /* fall through */ }
  try { (window.top as Window).location.href = url; } catch { window.open(url, "_blank"); }
};

// ------------------------------------------------------------------ popups + relay
// stay: true -> after the popup finishes, the iframe stays on the current page (no reload)
// and fires EMBEDDED_POPUP_DONE with the popup's final URL, e.g. "...?linkedin=connected".
type ExternalTarget = string | { social: string; stay?: boolean };
export const EMBEDDED_POPUP_DONE = "ocesha-embedded-popup-done";

const getHandoffCode = async (): Promise<string> => {
  const { data } = await axios.post(`${API}/embedded/handoff`);
  return data.code;
};

/**
 * Open a step that can't run inside the iframe (Stripe Checkout, social login…) in a popup.
 * When the popup returns to Ocesha, the iframe navigates to that same URL, so existing
 * success pages (/success?session_id=…, ?linkedin=connected …) run inside SHOPLINE.
 */
export const openExternal = async (target: ExternalTarget) => {
  const relay = randomId();
  let popupUrl: string;
  try {
    if (typeof target === "string") {
      popupUrl = `/shopline/popup?${new URLSearchParams({ relay, to: target })}`;
    } else {
      const code = await getHandoffCode();
      popupUrl = `/shopline/popup?${new URLSearchParams({ relay, social: target.social, code })}`;
    }
  } catch (e) {
    console.error("[shopline] could not prepare popup", e);
    showOverlay({ state: "error", onRetry: () => openExternal(target) });
    return;
  }

  const popup = window.open(popupUrl, POPUP_NAME, POPUP_FEATURES);
  if (!popup) {
    // Popup blocked (no user gesture left): let the merchant open it with a click.
    showOverlay({
      state: "blocked",
      onOpen: () => {
        const w = window.open(popupUrl, POPUP_NAME, POPUP_FEATURES);
        if (w) watchRelay(relay, w, target);
      },
    });
    return;
  }
  watchRelay(relay, popup, target);
};

const watchRelay = (relay: string, popup: Window | null, target: ExternalTarget) => {
  showOverlay({
    state: "waiting",
    onFocus: () => { try { popup?.focus(); } catch { /* ignore */ } },
    onRetry: () => { stop(); openExternal(target); },
    onCancel: () => stop(),
  });
  const started = Date.now();
  let closedAt = 0;
  let stopped = false;
  const stop = () => { stopped = true; hideOverlay(); };

  const tick = async () => {
    if (stopped) return;
    try {
      const { data } = await axios.get(`${API}/embedded/relay/${relay}`);
      if (data?.path) {
        stopped = true;
        hideOverlay();
        try { popup?.close(); } catch { /* ignore */ }
        if (typeof target !== "string" && target.stay) {
          window.dispatchEvent(new CustomEvent(EMBEDDED_POPUP_DONE, { detail: { path: data.path } }));
          return;
        }
        window.location.assign(data.path);
        return;
      }
    } catch { /* keep polling */ }

    let closed = false;
    try { closed = !!popup?.closed; } catch { closed = false; }
    if (closed && !closedAt) closedAt = Date.now();
    if (closedAt && Date.now() - closedAt > 15000) {
      showOverlay({ state: "closed", onRetry: () => { stop(); openExternal(target); }, onCancel: () => stop() });
      stopped = true;
      return;
    }
    if (Date.now() - started > RELAY_MAX_MS) { stop(); return; }
    setTimeout(tick, RELAY_POLL_MS);
  };
  setTimeout(tick, RELAY_POLL_MS);
};

/** Use instead of `window.location.href = url` for external pages. */
export const goExternal = (url?: string | null) => {
  if (!url) return;
  if (isEmbedded()) openExternal(url);
  else window.location.href = url;
};

// ------------------------------------------------------------------ popup side
/** Called by /shopline/popup (top-level popup window) before leaving for Stripe / OAuth. */
export const rememberPopupRelay = (relay: string) => {
  const v = JSON.stringify({ relay, ts: Date.now() });
  ss.set(K.popupRelay, v);
  ls.set(K.popupRelay, v);
};

/**
 * Returns the relay id when this window is an Ocesha popup that has just come back
 * from Stripe / an OAuth provider. sessionStorage is per-tab, so normal Ocesha tabs
 * are never affected.
 */
export const getReturningPopupRelay = (): string | null => {
  if (isInIframe()) return null;
  if (window.location.pathname.startsWith("/shopline/popup")) return null;
  const parse = (v: string | null, maxAge: number) => {
    if (!v) return null;
    try {
      const o = JSON.parse(v);
      return o?.relay && Date.now() - o.ts < maxAge ? (o.relay as string) : null;
    } catch { return null; }
  };
  const fromSession = parse(ss.get(K.popupRelay), RELAY_MAX_MS);
  if (fromSession) return fromSession;
  // Some browsers drop sessionStorage when a provider isolates the window; accept the
  // localStorage copy only for a real popup (has an opener or our window name).
  const isPopupWindow = !!window.opener || window.name === POPUP_NAME;
  return isPopupWindow ? parse(ls.get(K.popupRelay), 15 * 60 * 1000) : null;
};

export const completePopupRelay = async (relay: string, path: string) => {
  ss.del(K.popupRelay);
  ls.del(K.popupRelay);
  await fetch(`${API}/embedded/relay/${encodeURIComponent(relay)}`, {
    method: "POST",
    body: new URLSearchParams({ path }),
  });
};

// ------------------------------------------------------------------ navigation guard
const isLogoutUrl = (u: URL) => {
  const d = process.env.REACT_APP_LOGOUT_DOMAIN;
  if (!d) return false;
  try { return new URL(d).host === u.host; } catch { return false; }
};

/**
 * Catches `window.location.href = "https://checkout.stripe.com/…"` (and similar) anywhere
 * in the dashboard while embedded, and opens it in the popup instead of breaking the iframe.
 * Uses the Navigation API (Chromium, Safari 26+, recent Firefox); the main flows are also
 * patched explicitly with goExternal()/openExternal() for older browsers.
 */
let guardInstalled = false;
export const installNavigationGuard = () => {
  const nav: any = (window as any).navigation;
  if (guardInstalled || !nav || !isInIframe()) return;
  guardInstalled = true;
  nav.addEventListener("navigate", (e: any) => {
    if (!isEmbedded() || !e.cancelable || e.hashChange || e.downloadRequest) return;
    let url: URL;
    try { url = new URL(e.destination.url); } catch { return; }
    if (url.origin === window.location.origin) return;
    if (!/^https?:$/.test(url.protocol)) return;
    if (API_BASE && url.origin === new URL(API_BASE).origin) return; // backend downloads etc.

    e.preventDefault();
    if (isLogoutUrl(url)) {
      window.location.assign("/shopline/app?ended=1");
    } else if (/(^|\.)myshopline\.com$|(^|\.)shopline\.com$/.test(url.hostname)) {
      topRedirect(url.href);
    } else {
      openExternal(url.href);
    }
  });
};

// ------------------------------------------------------------------ overlay (plain DOM)
type OverlayState = "waiting" | "blocked" | "closed" | "error";
interface OverlayOpts {
  state: OverlayState;
  onOpen?: () => void; onFocus?: () => void; onRetry?: () => void; onCancel?: () => void;
}
const OVERLAY_ID = "ocesha-shopline-overlay";

const hideOverlay = () => { document.getElementById(OVERLAY_ID)?.remove(); };

const showOverlay = (o: OverlayOpts) => {
  hideOverlay();
  const text: Record<OverlayState, [string, string]> = {
    waiting: ["Finish in the new window", "Complete this step in the window that just opened. Ocesha continues here automatically."],
    blocked: ["One more click", "Your browser blocked the window. Click below to open it."],
    closed: ["Window closed", "It looks like the window was closed before finishing."],
    error: ["Something went wrong", "We couldn't start this step. Please try again."],
  };
  const [title, body] = text[o.state];
  const wrap = document.createElement("div");
  wrap.id = OVERLAY_ID;
  wrap.setAttribute("role", "dialog");
  wrap.style.cssText = "position:fixed;inset:0;z-index:2147483000;background:rgba(6,8,15,.72);display:flex;align-items:center;justify-content:center;padding:16px;font-family:inherit";
  wrap.innerHTML = `
    <div style="max-width:400px;width:100%;background:#0F1117;color:#fff;border:1px solid rgba(255,255,255,.1);border-radius:14px;padding:24px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.5)">
      ${o.state === "waiting" ? '<div style="width:28px;height:28px;margin:0 auto 14px;border:3px solid rgba(75,165,235,.25);border-top-color:#4BA5EB;border-radius:50%;animation:ocspin 1s linear infinite"></div>' : ""}
      <div style="font-weight:700;font-size:17px;margin-bottom:6px">${title}</div>
      <div style="font-size:13.5px;line-height:1.55;color:rgba(255,255,255,.65);margin-bottom:18px">${body}</div>
      <div data-btns style="display:flex;flex-direction:column;gap:8px"></div>
    </div>
    <style>@keyframes ocspin{to{transform:rotate(360deg)}}</style>`;
  const btns = wrap.querySelector("[data-btns]") as HTMLElement;
  const add = (label: string, primary: boolean, fn?: () => void) => {
    if (!fn) return;
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = primary
      ? "padding:10px 14px;border:0;border-radius:8px;background:#4BA5EB;color:#fff;font-weight:600;font-size:14px;cursor:pointer"
      : "padding:9px 14px;border:1px solid rgba(255,255,255,.18);border-radius:8px;background:transparent;color:#fff;font-size:13.5px;cursor:pointer";
    b.onclick = fn;
    btns.appendChild(b);
  };
  if (o.state === "blocked") add("Open window", true, () => { hideOverlay(); o.onOpen?.(); });
  if (o.state === "waiting") add("Show the window", true, o.onFocus);
  if (o.state === "waiting" || o.state === "closed" || o.state === "error") add("Try again", o.state !== "waiting", o.onRetry);
  add("Cancel", false, o.onCancel || hideOverlay);
  document.body.appendChild(wrap);
};
